import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  ServiceType,
  ApiResponse,
  ServiceStats,
  PriceCalculation,
  TimeCalculation
} from '../models/ServiceType';
import { PagedResult, PaginationOptions } from '../shared/interfaces/pagination.interface';
import { ErrorService } from '../shared/services/error.service';

export type ServiceTypePagedResult = PagedResult<ServiceType>;

@Injectable({
  providedIn: 'root'
})
export class ServiceTypeService {
  private readonly baseUrl = environment.microservices.operation;

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) { }

  // ===== CRUD OPERATIONS =====

  /**
   * Criar novo tipo de serviço
   */
  create(service: Partial<ServiceType>): Observable<ApiResponse<ServiceType>> {
    return this.http.post<ApiResponse<ServiceType>>(
      `${this.baseUrl}/services`,
      service
    );
  }

  /**
   * Atualizar tipo de serviço
   */
  update(id: number, service: Partial<ServiceType>): Observable<ApiResponse<ServiceType>> {
    return this.http.put<ApiResponse<ServiceType>>(
      `${this.baseUrl}/services/${id}`,
      service
    );
  }

  /**
   * Buscar por ID
   */
  getById(id: number): Observable<ApiResponse<ServiceType>> {
    return this.http.get<ApiResponse<ServiceType>>(
      `${this.baseUrl}/services/${id}`
    );
  }

  /**
   * Listar todos (paginado)
   */
  getAll(options?: PaginationOptions): Observable<ServiceTypePagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());

    return this.http.get<any>(`${this.baseUrl}/services`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar tipos de serviço');
        return throwError(() => err);
      })
    );
  }

  /**
   * Excluir serviço (soft delete)
   */
  delete(id: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(
      `${this.baseUrl}/services/${id}`
    );
  }

  // ===== CONSULTAS ESPECÍFICAS =====

  /**
   * Listar apenas serviços ativos
   */
  getActive(options?: PaginationOptions): Observable<ServiceTypePagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());

    return this.http.get<any>(`${this.baseUrl}/services/active`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar serviços ativos');
        return throwError(() => err);
      })
    );
  }

  /**
   * Buscar por categoria
   */
  getByCategory(category: string, options?: PaginationOptions): Observable<ServiceTypePagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());

    return this.http.get<any>(
      `${this.baseUrl}/services/category/${encodeURIComponent(category)}`,
      { params }
    ).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar serviços por categoria');
        return throwError(() => err);
      })
    );
  }

  /**
   * Buscar por código
   */
  getByCode(code: string): Observable<ApiResponse<ServiceType>> {
    return this.http.get<ApiResponse<ServiceType>>(
      `${this.baseUrl}/services/code/${code}`
    );
  }

  /**
   * Alternar status ativo/inativo
   */
  toggleStatus(id: number): Observable<ApiResponse<void>> {
    return this.http.patch<ApiResponse<void>>(
      `${this.baseUrl}/services/${id}/toggle`,
      {}
    );
  }

  // ===== FILTROS =====

  /**
   * Serviços por tempo estimado (em minutos)
   */
  getByTime(maxMinutes: number, options?: PaginationOptions): Observable<ServiceTypePagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());

    return this.http.get<any>(
      `${this.baseUrl}/services/filter/time/${maxMinutes}`,
      { params }
    ).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar serviços por tempo');
        return throwError(() => err);
      })
    );
  }

  /**
   * Serviços por faixa de preço
   */
  getByPriceRange(minPrice: number, maxPrice: number, options?: PaginationOptions): Observable<ServiceTypePagedResult> {
    let params = new HttpParams()
      .set('min', minPrice.toString())
      .set('max', maxPrice.toString());
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());

    return this.http.get<any>(
      `${this.baseUrl}/services/filter/price`,
      { params }
    ).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar serviços por preço');
        return throwError(() => err);
      })
    );
  }

  /**
   * Serviços por margem de lucro mínima
   */
  getByMargin(minMargin: number, options?: PaginationOptions): Observable<ServiceTypePagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());

    return this.http.get<any>(
      `${this.baseUrl}/services/filter/margin/${minMargin}`,
      { params }
    ).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar serviços por margem');
        return throwError(() => err);
      })
    );
  }

  // ===== ESTATÍSTICAS =====

  /**
   * Obter estatísticas gerais
   */
  getStats(): Observable<ApiResponse<ServiceStats>> {
    return this.http.get<ApiResponse<ServiceStats>>(
      `${this.baseUrl}/services/stats`
    );
  }

  // ===== CÁLCULOS =====

  /**
   * Calcular preço final com margem
   */
  calculatePrice(id: number): Observable<ApiResponse<PriceCalculation>> {
    return this.http.get<ApiResponse<PriceCalculation>>(
      `${this.baseUrl}/services/${id}/calculate-price`
    );
  }

  /**
   * Calcular tempo total de múltiplos serviços
   */
  calculateTotalTime(serviceIds: number[]): Observable<ApiResponse<TimeCalculation>> {
    return this.http.post<ApiResponse<TimeCalculation>>(
      `${this.baseUrl}/services/calculate-total-time`,
      { serviceIds }
    );
  }

  // ===== MAPEAMENTO DE RESPOSTAS =====

  /**
   * Mapeia resposta paginada da API para PagedResult
   */
  private mapPagedResponse(response: any): ServiceTypePagedResult {
    // A API pode retornar diretamente como PagedResult ou dentro de uma estrutura ApiResponse
    if (response.items) {
      // Formato direto: { items: [], page: 1, pageSize: 50, totalItems: 100, totalPages: 2 }
      return {
        items: this.mapListFromDto(response.items),
        page: response.page || 1,
        pageSize: response.pageSize || 50,
        totalItems: response.totalItems || 0,
        totalPages: response.totalPages || 0
      };
    } else if (response.data && Array.isArray(response.data)) {
      // Formato antigo: { success: true, data: [], total: 100, page: 1, pageSize: 50 }
      return {
        items: this.mapListFromDto(response.data),
        page: response.page || 1,
        pageSize: response.pageSize || 50,
        totalItems: response.totalItems || response.total || 0,
        totalPages: response.totalPages || Math.ceil((response.totalItems || response.total || 0) / (response.pageSize || 50))
      };
    } else {
      // Fallback: tratar como array simples
      const items = Array.isArray(response) ? response : [];
      return {
        items: this.mapListFromDto(items),
        page: 1,
        pageSize: items.length,
        totalItems: items.length,
        totalPages: 1
      };
    }
  }

  /**
   * Mapeia lista de DTOs para ServiceType
   */
  private mapListFromDto(list: any[]): ServiceType[] {
    if (!list || !Array.isArray(list)) return [];
    return list.map(item => this.mapFromDto(item));
  }

  /**
   * Mapeia um DTO para ServiceType
   */
  private mapFromDto(dto: any): ServiceType {
    return {
      id: dto.Id || dto.id,
      name: dto.Name || dto.name || '',
      description: dto.Description || dto.description,
      category: dto.Category || dto.category,
      estimatedTimeMinutes: dto.EstimatedTimeMinutes || dto.estimatedTimeMinutes,
      baseCost: dto.BaseCost || dto.baseCost,
      currentCost: dto.CurrentCost || dto.currentCost,
      profitMarginPercentage: dto.ProfitMarginPercentage || dto.profitMarginPercentage,
      isActive: dto.IsActive !== undefined ? dto.IsActive : (dto.isActive !== undefined ? dto.isActive : true),
      code: dto.Code || dto.code,
      createdAt: dto.CreatedAt || dto.createdAt || new Date().toISOString(),
      updatedAt: dto.UpdatedAt || dto.updatedAt,
      createdBy: dto.CreatedBy || dto.createdBy,
      updatedBy: dto.UpdatedBy || dto.updatedBy
    };
  }
}
